import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Button, IconButton, Tooltip, Alert } from '@mui/material';

import {
	AddShoppingCart as AddShoppingCartIcon,
	RemoveShoppingCart as RemoveShoppingCartIcon
} from '@mui/icons-material';

import { useActivityShopUI } from 'hooks/useActivityShopUI';
import { useLocalization } from 'hooks/useLocalization';

import { Activity } from "interfaces/ActivityInterfaces";
import { ActivityInteractionData } from 'interfaces/ActivityShopUIInterfaces';
import 'react-day-picker/dist/style.css';
import GeneralDialog from 'components/shared/GeneralDialog';
import { motion } from 'framer-motion';

interface AddActivityButtonContainerProps {
	activity: Activity;
	selectedActivityTypeTab: 'private' | 'group';
	activityInteractionData: ActivityInteractionData;
}

function AddActivityButtonContainer({ activity, selectedActivityTypeTab, activityInteractionData }: AddActivityButtonContainerProps) {
	const { getStr } = useLocalization();
	const { addToCart, removeFromCart } = useActivityShopUI();
	const [ isAnimating, setIsAnimating] = useState<boolean>(false);
	const addToCartButtonRef = useRef<HTMLButtonElement>(null);
    const [addToCartButtonElement, setAddToCartButtonElement] = useState<HTMLButtonElement | null>(null);
	const [showActivityAddedDialog, setShowActivityAddedDialog] = useState<boolean>(false);


	useEffect(() => {
		let animatedCartButtonContainer = document.getElementById('tt-animated-add-to-cart-button-container');

		if (!animatedCartButtonContainer) {
			const animatedCartButtonContainer = document.createElement('div');
			animatedCartButtonContainer.id = 'tt-animated-add-to-cart-button-container';
			
			// Insert the div as the first child of the body
			const bodyElement = document.querySelector('body');
			if (bodyElement) {
				bodyElement.insertBefore(animatedCartButtonContainer, bodyElement.firstChild);
			}
		}
	}, []);

	if (!activityInteractionData) return null;
	const selectedDate = activityInteractionData.dateSelected[selectedActivityTypeTab];

	function handleAddToCartClick(selectedActivityTypeTab: string) {
		if (selectedDate) {
            if (selectedActivityTypeTab === 'group') {
                // Add availability check here
                const year = selectedDate.getFullYear();
                const month = selectedDate.getMonth() + 1; // getMonth() returns 0-indexed month
                const day = selectedDate.getDate();

                const availableYear = activityInteractionData.availableDates.group.find(y => y.year === year);
                if (availableYear) {
                    const availableMonth = availableYear.months.find(m => m.month === month);
                    if (availableMonth) {
                        const availableDay = availableMonth.days.find(d => d.dayNum === day);
                        if (availableDay) {
                            const isDepartureAvailable = availableDay.departures.some(departure =>
                                departure.groups.some(group => group.capacity > group.occupancy)
                            );

                            if (!isDepartureAvailable) {
								return; //Abort if no availability
                            }
                        } else {
							return; //Abort if no availability
                        }
                    } else {
						return; //Abort if no availability
                    }
                } else {
					return; //Abort if no availability
                }
            }

			switch (selectedActivityTypeTab) {
				case 'private':
				case 'group':
					addToCart(activity.id, selectedActivityTypeTab);
					break;
			
				default:
					throw new Error('Invalid activity type "' + selectedActivityTypeTab + '"');
			}

			setAddToCartButtonElement(addToCartButtonRef.current);
			setIsAnimating(true);
			setShowActivityAddedDialog(true);
		}
	}

	function handleRemoveClick() {
		removeFromCart(activity.id);
	}

	return(
		<>
			{
				activityInteractionData.isAdded ? (
					<AnimatedAddToCartButtonPortal addToCartButtonElement={addToCartButtonElement} isAnimating={isAnimating} setIsAnimating={setIsAnimating} />
				) : null
			}
			<div style={{ height: '40px' }} />
			{
				activityInteractionData.isAdded ? (
					activityInteractionData.addedAs === selectedActivityTypeTab ? ( //Tab matches activity type
						<Alert
							severity="success"
							action={
								<IconButton
									aria-label={getStr('Remove')}
									color="error"
									size="small"
									onClick={handleRemoveClick}
								>
								<Tooltip title={getStr('Remove')}>
									<RemoveShoppingCartIcon fontSize="inherit" />
								</Tooltip>
								</IconButton>
							}
						>
							{getStr((activityInteractionData.addedAs === 'private' ? 'Added' : 'Added'))}
						</Alert>
					) : ( //Tab does not match activity type
						<Alert
							severity="info">
							{getStr((activityInteractionData.addedAs === 'private' ? 'Added_as_private_activity' : 'Added_as_group_activity'))}
						</Alert>
					)
				) : (
					<>
						
						<Button
							ref={addToCartButtonRef}
							variant="contained"
							onClick={() => {handleAddToCartClick(selectedActivityTypeTab)}}
							startIcon={<AddShoppingCartIcon />}
							className="activity-add-to-cart-button"
							disabled={
								!selectedDate || 
								!activityInteractionData.totalPrice[selectedActivityTypeTab]
							}
						>
							{ getStr('Add_to_cart') }
						</Button>
					</>
				)
			}
			<GeneralDialog
				isOpen={showActivityAddedDialog}
				title={getStr('Added_to_cart')}
				content={getStr('ADDED_TO_CART_INFO')}
				onClose={() => { setShowActivityAddedDialog(false) }}
			/>
		</>
	);
}

interface AnimatedAddToCartButtonPortalProps {
	addToCartButtonElement: HTMLButtonElement | null;
	isAnimating: boolean;
	setIsAnimating: (isAnimating: boolean) => void;
}

function AnimatedAddToCartButtonPortal({ addToCartButtonElement, isAnimating, setIsAnimating }: AnimatedAddToCartButtonPortalProps) {
	
	const portalElement = document.getElementById('tt-animated-add-to-cart-button-container');

	return(
		<>
			{ portalElement ? createPortal(<AnimatedAddToCartButtonContainer addToCartButtonElement={addToCartButtonElement} isAnimating={isAnimating} setIsAnimating={setIsAnimating} />, portalElement) : null }
		</>
	);
}

interface AnimatedAddToCartButtonContainerProps {
	addToCartButtonElement: HTMLButtonElement | null;
	isAnimating: boolean;
	setIsAnimating: (isAnimating: boolean) => void;
}

function AnimatedAddToCartButtonContainer({ addToCartButtonElement, isAnimating, setIsAnimating }: AnimatedAddToCartButtonContainerProps) {
	const { getStr } = useLocalization();

	if (!isAnimating) {
		return null;
	}

	const onFinish = () => {
		setIsAnimating(false);
	};

	//Select element with CSS class "tt-cart-button"
	const cartButtonElement = document.querySelector('.tt-cart-button');
	const cartButtonRectangle = cartButtonElement?.getBoundingClientRect();
	const cartButtonWidth = cartButtonRectangle ? (cartButtonRectangle.width) : 0;
	const cartButtonHeight = cartButtonRectangle ? (cartButtonRectangle.height) : 0;

	const cartButtonCenterPosition = {
		x: cartButtonRectangle ? (cartButtonRectangle.left + (cartButtonWidth / 2)) : 0,
		y: cartButtonRectangle ? (cartButtonRectangle.top + (cartButtonHeight / 2)) : 0
	};

	const addToCartButtonRectangle = addToCartButtonElement?.getBoundingClientRect();
	const addToCartButtonWidth = addToCartButtonRectangle ? (addToCartButtonRectangle.width) : 0;
	const addToCartButtonHeight = addToCartButtonRectangle ? (addToCartButtonRectangle.height) : 0;
	
	const start = { x: addToCartButtonRectangle ? (addToCartButtonRectangle.left) : 0, y: addToCartButtonRectangle ? (addToCartButtonRectangle.y) : 0 };
	const end = {
		x: cartButtonCenterPosition.x - (addToCartButtonWidth / 2),
		y: cartButtonCenterPosition.y - (addToCartButtonHeight / 2)
	};

	const animationProps = {
		initial: {
		  opacity: 1,
		  x: start.x,
		  y: start.y,
		  scale: 1
		},
		animate: {
		  x: end.x,
		  y: end.y,
		  opacity: 1,
		  scale: 0.1,
		  transition: { duration: 0.5 }
		},
		onAnimationComplete: onFinish
	  };

	return (
		<>
			<div style={{
				position: 'fixed',
				zIndex: 1000000000,
				width: `${addToCartButtonWidth}px`,
				height: `${addToCartButtonHeight}px`
			}}>
				<motion.div {...animationProps}>
					<Button
						variant="contained"
						startIcon={<AddShoppingCartIcon />}
						className="activity-add-to-cart-button"
						sx={{
							zIndex: 100000000,
							transition: 'none', // Disable CSS transitions during animation
						}}
					>
						{ getStr('Add_to_cart') }
					</Button>
				</motion.div>
			</div>
		</>
	);
}

export default AddActivityButtonContainer;